import React from 'react';
import { ArrowRightIcon, CalendarDaysIcon, CheckIcon } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { InquiryForm } from '../components/forms/InquiryForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { programs } from '../data/programs';
import { site } from '../data/site';
import type { ProgramId } from '../types/content';
const programRoutes: Record<ProgramId, string> = {
  'little-braves': '/little-braves',
  kids: '/kids-taekwondo',
  'teens-adults': '/teens-adults',
  'competition-team': '/competition-team'
};
const programHeroPhotos: Record<ProgramId, {
  src: string;
  alt: string;
  position: string;
  mobilePosition: string;
  credit?: string;
}> = {
  'little-braves': {
    src: 'https://static.wixstatic.com/media/37c34b_6fdec3490f074c288a1ce8cc078d51be~mv2.jpeg',
    alt: 'Four young Bates Taekwondo students smiling in protective gear',
    position: '50% 30%',
    mobilePosition: '42% 50%'
  },
  kids: {
    src: 'https://static.wixstatic.com/media/37c34b_12383dcdf7c44ec492a8e2a0d5e41306~mv2.jpg',
    alt: 'A Bates coach kneeling beside a young student in protective Taekwondo gear',
    position: '50% 45%',
    mobilePosition: '53% 50%'
  },
  'teens-adults': {
    src: 'https://static.wixstatic.com/media/61e59f_37f5fba21ed14ce5a207a9a9807160bf~mv2.jpg',
    alt: 'Two Taekwondo practitioners working on a high kick with a training paddle',
    position: '50% 35%',
    mobilePosition: '90% 50%',
    credit: 'Photo: Gomez PhotoWorks'
  },
  'competition-team': {
    src: 'https://static.wixstatic.com/media/37c34b_67ed01afa9d64739aee7738ef05d143e~mv2.jpg',
    alt: 'Three young Bates Taekwondo athletes wearing medals beside the tournament mats',
    position: '50% 25%',
    mobilePosition: '45% 50%'
  }
};
const programGalleries: Partial<Record<ProgramId, {
  src: string;
  alt: string;
  width: number;
  height: number;
}[]>> = {
  'little-braves': [{
    src: 'https://static.wixstatic.com/media/37c34b_8be34a4d8bbe4013b926f58775df3394~mv2.jpg',
    alt: 'Two Little Braves students wearing red chest protectors on the training mats',
    width: 1262,
    height: 1264
  }],
  'competition-team': [{
    src: 'https://static.wixstatic.com/media/37c34b_4459babc4242442c8ac91ea31ac60ab5~mv2.jpg',
    alt: 'Two Taekwondo athletes exchanging kicks during a tournament match',
    width: 649,
    height: 543
  }, {
    src: 'https://static.wixstatic.com/media/37c34b_c783d22243584c0cbf22d277dd342ff5~mv2.png',
    alt: 'Four people in matching blue Taekwondo team shirts posing together at an event',
    width: 2038,
    height: 1283
  }, {
    src: 'https://static.wixstatic.com/media/37c34b_67ed01afa9d64739aee7738ef05d143e~mv2.jpg',
    alt: 'Three young Taekwondo athletes wearing medals beside the tournament mats',
    width: 2048,
    height: 1536
  }, {
    src: 'https://static.wixstatic.com/media/37c34b_5b331a88ef0141a9876fb8a8912cfbc7~mv2.jpg',
    alt: 'Bates team photo with AAU Nationals, Florida 2023 and USA Nationals lettering',
    width: 2048,
    height: 1365
  }]
};
export function ProgramPage({
  programId
}: {
  programId: ProgramId;
}) {
  const program = programs.find(item => item.id === programId)!;
  const gallery = programGalleries[programId];
  const heroPhoto = programHeroPhotos[programId];
  const isCompetition = programId === 'competition-team';
  const contactHref = isCompetition ? `${site.emailHref}?subject=${encodeURIComponent('Competition Team inquiry')}` : '/free-trial-class';
  return <PageShell title={program.name} eyebrow={program.audience} intro={program.summary} heroBackground={heroPhoto} heroAside={<InquiryForm context={program.name} data-id="element-716" data-mp="b427347k1rte" />} data-id="element-715" data-mp="2vv2ojdpywfbm">
      <section className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16" aria-labelledby="program-training-title" data-id="element-717" data-mp="b2r2512die0p">
        <div data-id="element-718" data-mp="64pm6p08uutg">
          <h2 id="program-training-title" className="font-display text-3xl font-bold uppercase leading-tight sm:text-4xl" data-id="element-719" data-mp="67brehznwy8i">
            {isCompetition ? 'Train with a competitive goal' : 'What happens in class'}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted" data-id="element-720" data-mp="1bdlgc676hh47">{program.description}</p>
          <ul className="mt-7 space-y-4" data-id="element-721" data-mp="1gceork7lyhxi">
            {program.practices.map(practice => <li key={practice} className="flex items-start gap-3 leading-relaxed" data-id="element-722" data-mp="1kdzzp00l0m52">
                <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-723" data-mp="1kc5pafmxscf8" />
                {practice}
              </li>)}
          </ul>
          <div className="mt-8 border-t border-line pt-6" data-id="element-724" data-mp="1u4bgvo9a0rtw">
            <h3 className="text-sm font-semibold uppercase tracking-[0.12em]" data-id="element-725" data-mp="1z3foiagjyhi4">A good fit for</h3>
            <p className="mt-3 leading-relaxed text-muted" data-id="element-726" data-mp="24290fyg6y0g6">{program.goodFit}</p>
          </div>
          <ButtonLink href={contactHref} className="mt-8 w-full !whitespace-normal py-3 text-center sm:w-auto" icon={<ArrowRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-728" data-mp="3sm4vb92hhyqh" />} data-id="element-727" data-mp="283b4csl1z0oh">
            {program.ctaLabel}
          </ButtonLink>
        </div>
        <figure className="overflow-hidden rounded bg-coal" data-id="element-729" data-mp="3oigpovxexd7v">
          <img src={program.image} alt={program.imageAlt} className={`aspect-[4/3] w-full ${program.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`} style={{
          objectPosition: program.imagePosition
        }} data-id="element-730" data-mp="3jk5d4dfqgoly" />
          <figcaption className="border-t border-white/10 px-5 py-4 text-sm text-white/75" data-id="element-731" data-mp="b554x400eap5">
            {program.name} at Bates Taekwondo in Albuquerque
          </figcaption>
        </figure>
      </section>

      {programId === 'little-braves' && <section aria-labelledby="little-braves-progress-title" className="grid gap-8 border-t border-line pt-10 lg:grid-cols-2 lg:gap-16" data-id="element-732" data-mp="1eitllzbs6c9">
          <div data-id="element-733" data-mp="u3zlmij19l7y">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-734" data-mp="u49a21ofjkbj">Small steps. Growing independence.</p>
            <h2 id="little-braves-progress-title" className="mt-3 font-display text-3xl font-bold uppercase sm:text-4xl" data-id="element-735" data-mp="2ocwfbqxe1blh">A foundation for ages 3–5</h2>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-736" data-mp="2sf9hy6gu8te1">Children work on posture, balance and coordination while learning to follow directions. Four introductory belt ranks give them achievable goals as they grow more comfortable trying skills on their own.</p>
          </div>
          <div className="rounded border border-line bg-white p-6 sm:p-8" data-id="element-737" data-mp="24mj8q50bko7g">
            <h3 className="flex items-center gap-3 font-display text-2xl font-semibold uppercase" data-id="element-738" data-mp="29nqexzmtdfc0"><CalendarDaysIcon className="h-6 w-6 text-brand" aria-hidden="true" data-id="element-739" data-mp="1vo7l8bur26j8" />Little Braves class options</h3>
            <dl className="mt-5 space-y-4 text-[15px]" data-id="element-740" data-mp="20n23ncot1t2t">
              <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-line pb-3" data-id="element-741" data-mp="3q1gmjgd56lqe"><dt className="font-semibold" data-id="element-742" data-mp="3v0a018sficqk">Group 1 · Mon & Wed</dt><dd data-id="element-743" data-mp="3uyz0jl60nr59">4:30 p.m.</dd></div>
              <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-line pb-3" data-id="element-744" data-mp="3uxxl74hywihq"><dt className="font-semibold" data-id="element-745" data-mp="8wqaf986ldnx">Group 2 · Tue & Thu</dt><dd data-id="element-746" data-mp="40srcv5j9t7g">4:30 p.m.</dd></div>
              <div data-id="element-747" data-mp="41kklzbnreuk"><dt className="font-semibold" data-id="element-748" data-mp="3xn31i4qavcs">Both groups · Friday, 4:30 p.m.</dt><dd className="mt-1 text-muted" data-id="element-749" data-mp="hrxvoajuj9o4">Sparring · gear required</dd></div>
            </dl>
            <p className="mt-5 text-sm leading-relaxed text-muted" data-id="element-750" data-mp="czd1h7x12uk2">Contact us to confirm current availability and the right group for your child.</p>
          </div>
        </section>}

      {isCompetition && <section aria-labelledby="competition-path-title" className="grid gap-8 border-t border-line pt-10 lg:grid-cols-2 lg:gap-16" data-id="element-751" data-mp="mppk2ula8dnk">
          <div data-id="element-752" data-mp="wof5gxce0g8k">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-753" data-mp="wnvn5fitrpu0">An additional pathway</p>
            <h2 id="competition-path-title" className="mt-3 font-display text-3xl font-bold uppercase sm:text-4xl" data-id="element-754" data-mp="32glmliifqeho">From the training floor to the tournament</h2>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-755" data-mp="2yi4plrsqakdz">Team training includes focused sparring preparation and opportunities to compete around the country. Athletes put in committed work alongside teammates and build friendships along the way.</p>
          </div>
          <div className="rounded border border-line bg-white p-6 sm:p-8" data-id="element-756" data-mp="lnlegulf0m5">
            <h3 className="font-display text-2xl font-semibold uppercase" data-id="element-757" data-mp="4lnxhuu7yzq1">Interested in joining?</h3>
            <p className="mt-4 leading-relaxed text-muted" data-id="element-758" data-mp="342w0isxxrmd6">Talk with a coach about your experience, competitive goals, current training times and next steps.</p>
            <a href={site.phoneHref} className="mt-5 inline-flex min-h-[44px] items-center font-semibold text-brand underline underline-offset-4" data-id="element-759" data-mp="3d2irslwybbsi">Call {site.phoneDisplay}</a>
          </div>
        </section>}

      {gallery && <section aria-labelledby="program-gallery-title" data-id="element-760" data-mp="sb9vwg2rpawz">
          <h2 id="program-gallery-title" className="font-display text-3xl font-bold uppercase sm:text-4xl" data-id="element-761" data-mp="xbedk9fewbos">
            {isCompetition ? 'Team moments' : 'Little Braves on the mat'}
          </h2>
          <div className={`mt-7 grid gap-5 ${gallery.length > 1 ? 'sm:grid-cols-2' : 'max-w-2xl'}`} data-id="element-762" data-mp="17h5zj99t7cmv">
            {gallery.map(photo => <a key={photo.src} href={photo.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full photo: ${photo.alt} (opens in a new tab)`} className="flex items-center justify-center overflow-hidden rounded border border-line bg-coal" data-id="element-763" data-mp="xognac6fpuqh">
                <img src={photo.src} alt={photo.alt} width={photo.width} height={photo.height} loading="lazy" className={`w-full object-contain ${gallery.length > 1 ? 'aspect-[4/3]' : 'h-auto'}`} data-id="element-764" data-mp="eqe3qcwnoq02" />
              </a>)}
          </div>
        </section>}

      <section className="grid gap-6 sm:grid-cols-2" aria-label="Plan your training" data-id="element-765" data-mp="1eff8uynjan1f">
        <a href="/schedule" className="group rounded border border-line bg-white p-6 transition-colors hover:border-ink sm:p-8" data-id="element-766" data-mp="1eh9w4jma2r8o">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-767" data-mp="1oe4un2ijwrmy">Plan your week</p>
          <h2 className="mt-3 flex items-center justify-between gap-3 font-display text-2xl font-bold uppercase" data-id="element-768" data-mp="1je08owyvj4pc">Class schedule <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-769" data-mp="vpz7ya7xsskh" /></h2>
          <p className="mt-3 leading-relaxed text-muted" data-id="element-770" data-mp="qrnwxw5woc0o">See the school schedule and confirm the class that fits.</p>
        </a>
        <a href={programId === 'little-braves' ? '/free-trial-class' : '/more'} className="group rounded border border-line bg-white p-6 transition-colors hover:border-ink sm:p-8" data-id="element-771" data-mp="10o0skzl0gzy2">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-772" data-mp="cwd5ksqve912">{programId === 'little-braves' ? 'Get started' : 'Practice between classes'}</p>
          <h2 className="mt-3 flex items-center justify-between gap-3 font-display text-2xl font-bold uppercase" data-id="element-773" data-mp="2jlx1eg8kjipb">{programId === 'little-braves' ? 'Your first class' : 'Poomsae videos'} <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-774" data-mp="2amjy65b1fqd1" /></h2>
          <p className="mt-3 leading-relaxed text-muted" data-id="element-775" data-mp="2eoexj49erto6">{programId === 'little-braves' ? 'Arrange a free trial and meet the coaches.' : 'Review forms and belt-test practice resources.'}</p>
        </a>
      </section>

      <nav aria-label="Other Taekwondo programs" className="border-t border-line pt-8" data-id="element-776" data-mp="37bhu3quixubs">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-muted" data-id="element-777" data-mp="2tc008x2dfqvk">Explore our other programs</h2>
        <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2" data-id="element-778" data-mp="2ydpgcr4gauqi">
          {programs.filter(item => item.id !== programId).map(item => <a key={item.id} href={programRoutes[item.id]} className="inline-flex min-h-[44px] items-center gap-2 font-semibold underline underline-offset-4 hover:text-brand" data-id="element-779" data-mp="1mwz5gup31eqk">{item.name}<ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-780" data-mp="3ong6rrofg0l3" /></a>)}
        </div>
      </nav>
    </PageShell>;
}